import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { voice } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Button, Text, Title, SimpleGrid, Skeleton } from '@repletoryreach/sdk-ui';
import { PageHeader, Alert, EmptyState, Tabs } from '@repletoryreach/sdk-ui/data-display';
import { NumberInput, Switch, Textarea, Checkbox } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../i18n/I18nProvider';
import { useVoiceStatus } from '../../lib/voice';
import { StatusPill } from '../../components/panel';
import { formatCount } from '../../lib/format';
import { Numbers } from './components/Numbers';
import { Calls } from './components/Calls';
import { Team } from './components/Team';
import { Applications } from './components/Applications';
import './voice.css';

const MUTED = 'var(--rr-color-text-muted)';

/**
 * Voice: phone calls answered by the assistant.
 *
 * Built switched off (2 October 2026). This page exists only when the service
 * says voice is available; otherwise it says, plainly, that there is nothing
 * here. When it is available the page follows where the account stands: not
 * started (accept the terms and ask for it), waiting for approval, suspended
 * (with the reason given), or on, where the account sets its own limits and
 * its recording choice inside what it is allowed.
 */
export default function VoicePage() {
  const t = useT();
  const { status, available, isPending, refetch } = useVoiceStatus();

  if (isPending) return <Stack gap="lg"><PageHeader title={t('voice.title')} description={t('voice.description')} withDivider={false} /><Skeleton height={180} /></Stack>;
  if (!available || !status) {
    return (
      <Stack gap="lg">
        <PageHeader title={t('voice.title')} description={t('voice.description')} withDivider={false} />
        <Card withBorder><EmptyState title={t('voice.unavailable.title')} description={t('voice.unavailable.body')} /></Card>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <PageHeader title={t('voice.title')} description={t('voice.description')} withDivider={false} />
      {status.tenant_status === 'off' && <GetStarted status={status} onDone={() => void refetch()} />}
      {status.tenant_status === 'applied' && (
        <Card withBorder data-testid="voice-applied">
          <Stack gap="xs">
            <Group gap="sm"><Title order={4}>{t('voice.applied.title')}</Title><StatusPill tone="submitted">{t('voice.state.applied')}</StatusPill></Group>
            <Text role="bodySmall" c={MUTED}>{t('voice.applied.body')}</Text>
          </Stack>
        </Card>
      )}
      {/* Waiting to be switched on is also when an account with no number of its own asks us for one. */}
      {status.tenant_status === 'applied' && <Applications status={status} />}
      {status.tenant_status === 'suspended' && (
        <Alert color="error" role="alert" data-testid="voice-suspended">
          <Stack gap="xs">
            <Text role="bodySmall" fw={650}>{t('voice.suspended.title')}</Text>
            <Text role="bodySmall">{status.suspended_reason ?? t('voice.suspended.noReason')}</Text>
            <Text role="caption">{t('voice.suspended.help')}</Text>
          </Stack>
        </Alert>
      )}
      {status.tenant_status === 'suspended' && <Calls />}
      {status.tenant_status === 'active' && <ActiveVoice status={status} />}
    </Stack>
  );
}

/** Voice once it is on: numbers first (nothing rings without one), then the calls, then the account's own limits. */
function ActiveVoice({ status }: { status: voice.VoiceStatus }) {
  const t = useT();
  // A link from the inbox (?call=<id>) opens the Calls tab on that call.
  const [tab, setTab] = useState(() => (new URLSearchParams(window.location.search).get('call') ? 'calls' : 'numbers'));
  return (
    <>
      <Card withBorder data-testid="voice-active">
        <Group justify="space-between" align="flex-start">
          <Stack gap="xs">
            <Group gap="sm"><Title order={4}>{t('voice.active.title')}</Title><StatusPill tone="active">{t('voice.state.active')}</StatusPill></Group>
            <Text role="bodySmall" c={MUTED}>{t('voice.active.body')}</Text>
          </Stack>
        </Group>
        {status.outbound_blocked && (
          <Alert color="warning">{t('voice.active.outboundBlocked', { reason: status.outbound_blocked_reason ?? '' })}</Alert>
        )}
      </Card>
    <Tabs value={tab} onChange={setTab}>
      <Tabs.List>
        <Tabs.Tab value="numbers">{t('voice.tabs.numbers')}</Tabs.Tab>
        <Tabs.Tab value="team">{t('voice.tabs.team')}</Tabs.Tab>
        <Tabs.Tab value="calls">{t('voice.tabs.calls')}</Tabs.Tab>
        <Tabs.Tab value="settings">{t('voice.tabs.settings')}</Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel value="numbers"><div className="rr-voice__panel"><Numbers status={status} /></div></Tabs.Panel>
      <Tabs.Panel value="team"><div className="rr-voice__panel"><Team status={status} /></div></Tabs.Panel>
      <Tabs.Panel value="calls"><div className="rr-voice__panel"><Calls /></div></Tabs.Panel>
      <Tabs.Panel value="settings"><div className="rr-voice__panel"><Stack gap="lg"><ActiveSettings status={status} /></Stack></div></Tabs.Panel>
    </Tabs>
    </>
  );
}

function GetStarted({ status, onDone }: { status: voice.VoiceStatus; onDone: () => void }) {
  const t = useT();
  const [accepted, setAccepted] = useState(false);
  const start = useMutation({
    mutationFn: () => voice.start(status.terms_version!),
    onSuccess: (res) => {
      notifySuccess(t(res.data.status === 'active' ? 'voice.start.doneActive' : 'voice.start.doneApplied'));
      onDone();
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'))
  });

  return (
    <Card withBorder data-testid="voice-start">
      <Stack gap="md">
        <Stack gap="xs">
          <Title order={4}>{t('voice.start.title')}</Title>
          <Text role="bodySmall" c={MUTED}>{t('voice.start.body')}</Text>
        </Stack>
        <ul className="rr-voice__points">
          <li><Text role="bodySmall">{t('voice.start.point.answer')}</Text></li>
          <li><Text role="bodySmall">{t('voice.start.point.transfer')}</Text></li>
          <li><Text role="bodySmall">{t('voice.start.point.prepaid')}</Text></li>
          <li><Text role="bodySmall">{t('voice.start.point.privacy')}</Text></li>
        </ul>
        {status.terms_version ? (
          <>
            <Checkbox
              checked={accepted}
              onChange={(checked) => setAccepted(checked)}
              label={t('voice.start.accept', { version: status.terms_version })}
            />
            {status.terms_url && (
              <Text role="caption" c={MUTED}>
                <a href={status.terms_url} target="_blank" rel="noreferrer">{t('voice.start.readTerms')}</a>
              </Text>
            )}
            <Group>
              <Button loading={start.isPending} disabled={!accepted} onClick={() => start.mutate()}>{t('voice.start.go')}</Button>
            </Group>
          </>
        ) : (
          <Alert color="info">{t('voice.start.noTerms')}</Alert>
        )}
      </Stack>
    </Card>
  );
}

function ActiveSettings({ status }: { status: voice.VoiceStatus }) {
  const t = useT();
  const qc = useQueryClient();
  const settings = useQuery({ queryKey: ['voice-settings'], queryFn: () => voice.getSettings() });
  const row = settings.data?.data;

  const [daily, setDaily] = useState<number | ''>('');
  const [hourly, setHourly] = useState<number | ''>('');
  const [minutes, setMinutes] = useState<number | ''>('');
  const [calls, setCalls] = useState<number | ''>('');
  const [recording, setRecording] = useState(false);
  const [notice, setNotice] = useState('');
  const [retention, setRetention] = useState<number | ''>('');

  useEffect(() => {
    if (!row) return;
    setDaily(row.settings.limits.daily_cap_minor / 100);
    setHourly(row.settings.limits.hourly_cap_minor / 100);
    setMinutes(row.settings.limits.max_call_minutes);
    setCalls(row.settings.limits.concurrent_calls);
    setRecording(row.settings.recording.enabled);
    setNotice(row.settings.recording.notice);
    setRetention(row.settings.recording.retention_days);
  }, [row]);

  const save = useMutation({
    mutationFn: () => voice.updateSettings({
      limits: {
        daily_cap_minor: daily === '' ? undefined : Math.round(daily * 100),
        hourly_cap_minor: hourly === '' ? undefined : Math.round(hourly * 100),
        max_call_minutes: minutes === '' ? undefined : minutes,
        concurrent_calls: calls === '' ? undefined : calls
      },
      recording: { enabled: recording, notice, retention_days: retention === '' ? undefined : retention }
    }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['voice-settings'] }); notifySuccess(t('voice.settings.saved')); },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'))
  });

  if (settings.isPending) return <Skeleton height={220} />;
  if (settings.error || !row) return <Alert color="error" role="alert">{(settings.error as Error | null)?.message ?? t('common.unknownError')}</Alert>;
  const allowed = row.limits;
  const credits = (minor: number) => formatCount(Math.round(minor / 100));

  return (
    <>
      <Card withBorder data-testid="voice-limits">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>{t('voice.limits.title')}</Title>
            <Text role="bodySmall" c={MUTED}>{t('voice.limits.body')}</Text>
          </Stack>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <NumberInput label={t('voice.limits.daily')} description={t('voice.limits.upTo', { max: credits(allowed.daily_cap_minor) })} value={daily} min={0} max={allowed.daily_cap_minor / 100} onChange={(v) => setDaily(typeof v === 'number' ? v : '')} />
            <NumberInput label={t('voice.limits.hourly')} description={t('voice.limits.upTo', { max: credits(allowed.hourly_cap_minor) })} value={hourly} min={0} max={allowed.hourly_cap_minor / 100} onChange={(v) => setHourly(typeof v === 'number' ? v : '')} />
            <NumberInput label={t('voice.limits.minutes')} description={t('voice.limits.upToMinutes', { max: allowed.max_call_minutes })} value={minutes} min={1} max={allowed.max_call_minutes} onChange={(v) => setMinutes(typeof v === 'number' ? v : '')} />
            <NumberInput label={t('voice.limits.calls')} description={t('voice.limits.upToCalls', { max: allowed.concurrent_calls })} value={calls} min={1} max={allowed.concurrent_calls} onChange={(v) => setCalls(typeof v === 'number' ? v : '')} />
          </SimpleGrid>
          <Text role="caption" c={MUTED}>{t(allowed.international ? 'voice.limits.internationalOn' : 'voice.limits.internationalOff')}</Text>
        </Stack>
      </Card>

      <Card withBorder data-testid="voice-recording">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>{t('voice.recording.title')}</Title>
            <Text role="bodySmall" c={MUTED}>{t('voice.recording.body')}</Text>
          </Stack>
          {status.switches.recording ? (
            <>
              <Switch label={t('voice.recording.enable')} description={t('voice.recording.enableHint')} checked={recording} onChange={(v) => setRecording(v)} />
              {recording && (
                <>
                  <Textarea label={t('voice.recording.notice')} description={t('voice.recording.noticeHint')} value={notice} maxLength={300} minRows={2} onChange={(e) => setNotice(e.currentTarget.value)} />
                  {/* The notice is made into speech when saved; without it, calls a person answers or places are not recorded. */}
                  {row?.notice_audio === 'ready' && <Text role="caption" c={MUTED} data-testid="voice-notice-audio">{t('voice.recording.noticeReady')}</Text>}
                  {row?.notice_audio === 'unavailable' && <Alert color="warning" data-testid="voice-notice-audio">{t('voice.recording.noticeUnavailable')}</Alert>}
                  <NumberInput label={t('voice.recording.retention')} description={t('voice.recording.retentionHint')} value={retention} min={1} onChange={(v) => setRetention(typeof v === 'number' ? v : '')} />
                </>
              )}
            </>
          ) : (
            <Text role="bodySmall" c={MUTED}>{t('voice.recording.unavailable')}</Text>
          )}
          <Group justify="space-between">
            <Text role="caption" c={MUTED}><Link to="/billing">{t('voice.settings.billingLink')}</Link></Text>
            <Button loading={save.isPending} onClick={() => save.mutate()}>{t('action.save')}</Button>
          </Group>
        </Stack>
      </Card>
    </>
  );
}
